import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App as AntdApp, Button, Checkbox, InputNumber, Modal, Select, Tooltip } from 'antd';

import { LOCK } from '../icons';
import SortableList from '../common/SortableList';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { deleteTableLayout, getCardLayouts, putTableLayout } from '../../services/cardLayouts';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { LayoutTierSwitch, TIER_NOTE, layoutTierAccess, preferredTier, tiersBelow } from '../cards/layoutTiers';
import {
	MAX_FROZEN,
	SYSTEM_COLUMNS,
	TABLE_SORT_SYSTEM,
	byNameOf,
	columnsFromEditorRows,
	editorRowsFor,
	frozenCount,
	reorderEditorRows,
	sameColumns,
	staleDisplays,
	staleTableColumns,
	tableSortIsValid,
} from './cardTableModel';
import { GROUP_DISPLAY_FIRST_DEFAULT, GROUP_DISPLAY_FIRST_MAX, visibleColumns } from '../../utils/repeatingGroupEntries';
import { columnsOf, isRepeatingGroup } from '../../config/repeatingGroups';

/**
 * "Columns…" — which columns the item table shows for ONE card, in what
 * order, and which stay frozen at the left while the rest scroll. The
 * editor for one TIER of a `table` card layout, with the tile editor's tier
 * switch (./cards/layoutTiers): "Just me" for anyone who can read the
 * collection, "This collection" for its managers, "This card" for whoever
 * may edit the card. Saving is immediate, as the tile layout's is.
 *
 * A row per column the card COULD show: a grip to drag it, a Show box, the
 * name and a Freeze toggle. Reordering is the platform's drag list
 * (common/SortableList, dnd-kit): one drag moves a column any distance, and
 * the grip is a keyboard control too — Space picks the row up, the arrows
 * move it, Space drops, Esc puts it back (live pass, 2026-09-26: ↑ ↓ buttons
 * took a click per place). The title is pinned first, always shown and
 * frozen, with no grip: it is the row's identity.
 *
 * Save and Reset CLOSE the modal once they land — the operation is done,
 * and a Cancel after a Save read as "undo". A refusal keeps it open.
 */
export default function TableColumnsModal({
	open,
	onClose,
	card,
	elementDefs = [],
	collection = null,
	collectionId = null,
	// The columns on screen now — what an unarranged tier starts from.
	currentColumns = [],
	onSaved,
}) {
	const { message } = AntdApp.useApp();
	const cardId = card?._id ? String(card._id) : null;

	const [tier, setTier] = useState('user');
	const [stored, setStored] = useState(null); // every tier's row
	// null until seeded for the tier on screen, so the list never draws empty
	// for the one render between a load landing and its seed.
	const [rows, setRows] = useState(null);
	// The tier's default order — { field, direction } or null — seeded with
	// the rows, a stale one already cleared.
	const [sort, setSort] = useState(null);
	const [saving, setSaving] = useState(false);
	const [loadError, setLoadError] = useState(null);

	const byName = useMemo(() => byNameOf(elementDefs), [elementDefs]);

	const { allowed: tierAllowed, refusal: tierRefusal } = useMemo(
		() => layoutTierAccess({ card, collection, collectionId }),
		[card, collection, collectionId]
	);

	const load = useCallback(() => {
		if (!open || !cardId) return;
		setStored(null);
		setRows(null);
		setLoadError(null);
		getCardLayouts(cardId, { collectionId, kind: 'table' })
			.then(({ layouts }) => setStored(layouts))
			.catch(error => setLoadError(error));
	}, [open, cardId, collectionId]);

	useEffect(load, [load]);

	useEffect(() => {
		if (open) setTier(preferredTier(tierAllowed));
	}, [open, tierAllowed]);

	const chooseTier = useCallback((next) => {
		setRows(null);
		setTier(next);
	}, []);

	const storedFor = useCallback(key => (stored || []).find(row => row.scope === key) || null, [stored]);

	// A tier that holds nothing starts from what it inherits — and a card
	// nobody has arranged, from the columns on screen — so "arrange it for me"
	// begins from what the reader is looking at.
	const inherited = useMemo(() => {
		for (const key of tiersBelow(tier)) {
			const row = storedFor(key);
			if (row) return { scope: key, columns: row.body?.columns || [], sort: row.body?.sort };
		}
		return null;
	}, [tier, storedFor]);

	const own = storedFor(tier);
	const baseline = own ? (own.body?.columns || []) : (inherited ? inherited.columns : currentColumns);
	const baselineSort = own ? (own.body?.sort || null) : (inherited?.sort || null);

	// A stored row can outlive the fields it names: nothing on the API
	// cleans a table layout when its card loses an element, and re-sending
	// one is refused (400 unknown_element / invalid_sort) — which left
	// "Reset" as the only way out of the tier (card-table review W2). The
	// editor drops them before it shows anything (editorRowsFor) and says so.
	const staleColumns = staleTableColumns(baseline, elementDefs);
	// …and a repeating group's cell can name a column that is gone (or hidden
	// from this person since): the editor drops the choice, the group keeps
	// its count (KPR-683).
	const staleCells = staleDisplays(baseline, elementDefs);
	const staleSort = Boolean(baselineSort) && !tableSortIsValid(baselineSort, elementDefs);

	useEffect(() => {
		if (stored === null) return;
		setRows(editorRowsFor(baseline, elementDefs));
		setSort(baselineSort && tableSortIsValid(baselineSort, elementDefs)
			? { field: baselineSort.field, direction: baselineSort.direction || 'desc' }
			: null);
	// Re-seed on a tier switch or a reload, never on every render.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stored, tier, elementDefs]);

	const nextColumns = columnsFromEditorRows(rows || []);
	const sortKey = value => (value?.field ? `${value.field}:${value.direction || 'desc'}` : '');
	// A tier of one's own whose stored row named a lost field is dirty as it
	// stands: Save is how the cleaned row is kept.
	const dirty = !own
		|| staleColumns.length > 0
		|| staleCells.length > 0
		|| staleSort
		|| sortKey(sort) !== sortKey(baselineSort)
		|| !sameColumns(nextColumns, columnsFromEditorRows(editorRowsFor(baseline, elementDefs)));
	const canWriteTier = tierAllowed[tier];
	const frozen = frozenCount(rows || []);

	// Unsaved work is a change to what the tier seeded — not `dirty`, which
	// is also true of a tier nobody has arranged yet.
	const seededSort = baselineSort && tableSortIsValid(baselineSort, elementDefs) ? baselineSort : null;
	const edited = rows !== null && (
		sortKey(sort) !== sortKey(seededSort)
		|| !sameColumns(nextColumns, columnsFromEditorRows(editorRowsFor(baseline, elementDefs)))
	);
	const { allowNavigation, guardDiscard } = useDirtyForm(edited, { enabled: open });
	// Cancel, the ✕, the mask and Esc all ask first when something was arranged.
	const handleCancel = useCallback(() => guardDiscard(() => onClose?.()), [guardDiscard, onClose]);

	const labelFor = row => (row.column.system
		? SYSTEM_COLUMNS[row.column.system]
		: (byName.get(row.column.element)?.label?.singular || row.column.element));

	const staleNotice = (() => {
		const parts = [];
		const n = staleColumns.length;
		if (n === 1) parts.push('1 column was removed because its field no longer exists.');
		else if (n > 1) parts.push(`${n} columns were removed because their fields no longer exist.`);
		if (staleCells.length) parts.push(staleCells.length === 1
			? 'A group\u2019s cell named a column that is gone, so it shows the number of entries instead.'
			: `${staleCells.length} groups\u2019 cells named columns that are gone, so they show the number of entries instead.`);
		if (staleSort) parts.push('The default order named a field that no longer exists, so it was cleared.');
		if (!parts.length) return null;
		if (own && canWriteTier) parts.push('Save to keep the change.');
		return parts.join(' ');
	})();

	const sortOptions = useMemo(() => [
		...[...byName.values()].map(def => ({ value: def.name, label: def?.label?.singular || def.name })),
		...TABLE_SORT_SYSTEM.map(field => ({ value: field, label: SYSTEM_COLUMNS[field] })),
	], [byName]);

	const patchRow = (key, patch) => setRows(current => (current || []).map(row => (row.key === key ? { ...row, ...patch } : row)));

	/**
	 * What a repeating group's cell shows (KPR-683): the number of entries, or a
	 * column of the first few entries. Only the columns this person may read
	 * are offered.
	 */
	const renderGroupDisplay = (row, def, label) => {
		const columns = visibleColumns(def);
		const value = row.display ? `column:${row.display.column}` : 'count';
		// A column the layout chose that this person is not shown stays in the
		// menu, said so: saving must not take it from whoever can read it.
		const chosenHidden = row.display && !columns.some(column => column.name === row.display.column)
			? columnsOf(def).find(column => column?.name === row.display.column)
			: null;
		return (
			<div className="table-columns__display">
				<Select
					className="table-columns__display-select"
					aria-label={`${label}: what the cell shows`}
					value={value}
					disabled={!row.shown || !canWriteTier}
					popupMatchSelectWidth={false}
					options={[
						{ value: 'count', label: 'Number of entries' },
						...columns.map(column => ({ value: `column:${column.name}`, label: `First entries: ${column.label?.singular || column.name}` })),
						...(chosenHidden ? [{ value: `column:${chosenHidden.name}`, label: `First entries: ${chosenHidden.label?.singular || chosenHidden.name} (hidden from you)`, disabled: false }] : []),
					]}
					onChange={(next) => {
						if (next === 'count') patchRow(row.key, { display: undefined });
						else patchRow(row.key, { display: { as: 'entries', column: next.slice('column:'.length), first: row.display?.first || GROUP_DISPLAY_FIRST_DEFAULT } });
					}}
				/>
				{row.display ? (
					<InputNumber
						className="table-columns__display-first"
						aria-label={`${label}: how many entries`}
						min={1}
						max={GROUP_DISPLAY_FIRST_MAX}
						precision={0}
						value={row.display.first}
						disabled={!row.shown || !canWriteTier}
						addonAfter="entries"
						onChange={first => patchRow(row.key, { display: { ...row.display, first: Number.isInteger(first) ? first : GROUP_DISPLAY_FIRST_DEFAULT } })}
					/>
				) : null}
			</div>
		);
	};

	// The drag list's rows, and the ones that never move: the title always,
	// and every row when this tier is not the reader's to write.
	const sortableRows = useMemo(() => (rows || []).map(row => ({ id: row.key, row })), [rows]);
	const fixedKeys = useMemo(
		() => (rows || []).filter(row => !canWriteTier || row.column.system === 'title').map(row => row.key),
		[rows, canWriteTier]
	);

	const save = async () => {
		setSaving(true);
		try {
			// The default order as the control below holds it — never the raw
			// stored one, which may name a field the card has lost.
			const validSort = sort && tableSortIsValid(sort, elementDefs) ? sort : null;
			await putTableLayout(cardId, {
				scope: tier,
				collectionId: tier === 'card' ? null : collectionId,
				body: { columns: nextColumns, ...(validSort ? { sort: validSort } : {}) },
			});
			message.success('Columns saved.');
			allowNavigation();
			onSaved?.();
			onClose?.();
		} catch (error) {
			message.error(error?.response?.data?.message || 'Saving the columns failed.');
		} finally {
			setSaving(false);
		}
	};

	const reset = async () => {
		setSaving(true);
		try {
			await deleteTableLayout(cardId, { scope: tier, collectionId: tier === 'card' ? null : collectionId });
			message.success(inherited ? `Reset to the ${inherited.scope} columns.` : 'Reset to every field.');
			allowNavigation();
			onSaved?.();
			onClose?.();
		} catch (error) {
			message.error(error?.response?.data?.message || 'Resetting the columns failed.');
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			title={`Columns · ${card?.label?.singular || card?.name || 'Card'}`}
			width={560}
			className="table-columns-modal"
			footer={[
				own ? (
					<Button key="reset" danger onClick={reset} disabled={!canWriteTier || saving}>
						{inherited ? `Reset to ${inherited.scope}` : 'Reset to every field'}
					</Button>
				) : null,
				<Button key="cancel" onClick={handleCancel}>Cancel</Button>,
				<Button key="save" type="primary" loading={saving} disabled={!canWriteTier || !dirty} onClick={save}>
					Save
				</Button>,
			].filter(Boolean)}
		>
			<LayoutTierSwitch value={tier} onChange={chooseTier} allowed={tierAllowed} refusal={tierRefusal} />
			<p className="form-panel__hint">
				{canWriteTier ? TIER_NOTE[tier] : tierRefusal[tier]}
				{' '}Changes here apply immediately.
			</p>

			{staleNotice && !loadError && stored !== null ? (
				<Alert type="warning" showIcon className="table-columns__stale" message={staleNotice} />
			) : null}

			<DialogLoadState
				loaded={stored !== null && rows !== null}
				error={Boolean(loadError)}
				label="this card’s columns"
				onRetry={load}
				skeleton={<Skeleton variant="list-row" count={6} />}
				minHeight={240}
			>
				<SortableList
					items={sortableRows}
					fixed={fixedKeys}
					ariaLabel="Columns"
					className="table-columns"
					getLabel={item => labelFor(item.row)}
					onReorder={keys => setRows(current => reorderEditorRows(current || [], keys))}
					renderItem={({ row }, { handle }) => {
						const isTitle = row.column.system === 'title';
						const label = labelFor(row);
						const freezeBlocked = !row.frozen && frozen >= MAX_FROZEN;
						return (
							<div
								className={`table-columns__row${row.shown ? '' : ' table-columns__row--hidden'}`}
								data-column={row.key}
							>
								{handle || <span className="table-columns__grip-space" aria-hidden="true" />}
								<Checkbox
									checked={row.shown}
									disabled={isTitle || !canWriteTier}
									onChange={event => patchRow(row.key, { shown: event.target.checked, ...(event.target.checked ? {} : { frozen: false }) })}
									aria-label={`Show ${label}`}
								/>
								<span className="table-columns__name">
									{label}
									{row.column.system ? <span className="table-columns__kind">keepr</span> : null}
								</span>
								{isTitle ? (
									<Tooltip title="The title is always first and frozen.">
										<span className="table-columns__pinned" role="img" aria-label="Always first and frozen"><LOCK /></span>
									</Tooltip>
								) : (
									<Tooltip title={freezeBlocked ? `At most ${MAX_FROZEN} columns stay frozen.` : (row.frozen ? 'Scroll with the rest' : 'Keep at the left while scrolling')}>
										<Button
											size="small"
											type={row.frozen ? 'default' : 'text'}
											className="table-columns__freeze"
											aria-pressed={row.frozen}
											disabled={!row.shown || freezeBlocked || !canWriteTier}
											onClick={() => patchRow(row.key, { frozen: !row.frozen })}
										>
											{row.frozen ? 'Frozen' : 'Freeze'}
										</Button>
									</Tooltip>
								)}
								{row.column.element && isRepeatingGroup(byName.get(row.column.element))
									? renderGroupDisplay(row, byName.get(row.column.element), label)
									: null}
							</div>
						);
					}}
				/>
			</DialogLoadState>

			{stored !== null && rows !== null && !loadError ? (
				<div className="table-columns__sort">
					<span className="table-columns__sort-label" id="table-columns-sort-label">Default order</span>
					<Select
						aria-labelledby="table-columns-sort-label"
						allowClear
						placeholder="The list’s own order"
						value={sort?.field || undefined}
						options={sortOptions}
						disabled={!canWriteTier}
						onChange={field => setSort(field ? { field, direction: sort?.direction || 'desc' } : null)}
						className="table-columns__sort-field"
					/>
					<Select
						aria-label="Direction"
						value={sort?.direction || 'desc'}
						options={[{ value: 'asc', label: 'Ascending' }, { value: 'desc', label: 'Descending' }]}
						disabled={!canWriteTier || !sort}
						onChange={direction => setSort(current => (current ? { ...current, direction } : current))}
						className="table-columns__sort-direction"
					/>
				</div>
			) : null}
		</Modal>
	);
}
